
import { defineComponent, ref, reactive, computed, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { vLoading, ElTabs, ElTabPane, ElMessage } from "element-plus";

import { StyleTag, UserTag } from "@/components/common";
import { AlbumId, AlbumModel, OnlineLinkKey, ArticleModel } from "@/typings";
import {
    getAlbumInfo,
    countViewAlbum,
    createArticleDraft,
    do_lock,
    albumRateHeart,
} from "@/services";
import { online_link_name } from "@/basics/enums";

import ArticleListItem from "@/components/ArticleListItem";
import { useStore, useUserInfo } from "@/store";
import AlbumEdit from "@/components/Editor/AlbumEdit.vue";
import AddToChartEditor from "@/components/Editor/AddToChartEditor.vue";
import wx from "@/basics/wx";
import ajax from "@/basics/fetch";
import wechatTitle from "@/directives/wechat-title";
import { CommentList } from "@/components/Comment";

export default defineComponent({
    components: {
        StyleTag,
        UserTag,
        ElTabs,
        ElTabPane,
        ArticleListItem,
        AlbumEdit,
        AddToChartEditor,
        CommentList,
    },
    directives: {
        vLoading,
        wechatTitle,
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const userInfo = useUserInfo();
        const id = ref("" as AlbumId);
        const dataGetted = ref(false);

        const loadings = reactive({
            info: false,
            articles: false,
        });

        const info = ref<AlbumModel | null>(null);

        const now_heart = ref(0);
        const heart_loading = ref(false);
        const rocknuts = ref<ArticleModel[]>([]);
        const activeOnlineLink = ref("");
        const albumEditor = ref();
        const activeOnlineLinkUrl = computed(() => {
            if (activeOnlineLink.value === "0" || activeOnlineLink.value === "" || !info.value) {
                return "";
            }
            // @ts-ignore
            return info.value.online_links[activeOnlineLink.value as OnlineLinkKey]?.url || "";
        });

        const handlerTimeout = ref(0);
        const refreshData = () => {
            if (!route.params.id) {
                return;
            }
            dataGetted.value = false;
            id.value = route.params.id as AlbumId;
            loadings.info = true;
            getAlbumInfo(id.value)
                .then((res) => {
                    dataGetted.value = true;
                    info.value = res.data.info;
                    now_heart.value = res.data.now_heart;
                    wx.wxConfig(ajax, {
                        title: `${info.value.main_artist_show} - ${info.value.title}`,
                        desc: `${info.value.main_artist_show} [${info.value.year}]`,
                        imgUrl: info.value.primary_img,
                    });
                    rocknuts.value = res.data.rocknuts.map((item) => {
                        return {
                            ...item,
                            album_data: res.data.info,
                        };
                    });

                    handlerTimeout.value = window.setTimeout(() => {
                        window.clearTimeout(handlerTimeout.value);
                        handlerTimeout.value = 0;
                        countViewAlbum(id.value);
                    }, 5000);
                })
                .finally(() => {
                    loadings.info = false;
                });
        };
        onUnmounted(() => {
            if (handlerTimeout.value) {
                window.clearTimeout(handlerTimeout.value);
            }
        });

        watch(
            () => {
                return route.params.id;
            },
            refreshData,
            {
                immediate: true,
            },
        );

        const store = useStore();

        const doHeart = (rate: number) => {
            if (!store.state.isLogin) {
                ElMessage.error("请先登录");
                return;
            }
            now_heart.value = rate;
            heart_loading.value = true;
            albumRateHeart({
                album_id: id.value,
                rate,
            })
                .then((json) => {
                    info.value!.hot_index = json.data.hot_index;
                    info.value!.counter_heart_1 = json.data.counter_heart_1;
                    info.value!.counter_heart_2 = json.data.counter_heart_2;
                })
                .finally(() => {
                    heart_loading.value = false;
                });
        };

        const handlerCreateArticle = () => {
            if ((info.value!.quality as any).index === 0) {
                ElMessage.error("请先协助完善专辑信息");
                return;
            }
            let flag = 0;
            for (const k in info.value!.online_links) {
                if ((info.value!.online_links as any)[k as any].url !== "") {
                    flag = 1;
                    break;
                }
            }
            if (flag === 0) {
                ElMessage.error("请先协助完善专辑信息，确保至少有一个试听链接");
                return;
            }
            createArticleDraft({
                type: "rocknuts",
                reference_id: info.value!._id,
            })
                .then((json) => {
                    router.push({
                        path:
                            "/article/write/rocknuts/" +
                            info.value!._id +
                            "/" +
                            json.data.article_id,
                    });
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                });
        };

        const dialogFormVisible = ref(false);
        const showEditer = () => {
            dialogFormVisible.value = true;
            albumEditor.value.isCombine = 0;
        };

        const doLockInfo = () => {
            do_lock("album", info.value!._id)
                .then((json) => {
                    info.value!.quality = json.data.info.quality;
                })
                .catch((json) => {
                    ElMessage.error(json.data.msg);
                });
        };

        const dialogAddRankFormVisible = ref(false);
        const handlerAddToRank = () => {
            dialogAddRankFormVisible.value = true;
        };

        const handlerCacheAdd = () => {
            store.commit("addAlbumCache", {
                _id: info.value!._id,
                title: info.value!.title,
                main_artist_show: info.value!.main_artist_show,
                main_artist: info.value!.main_artist,
                artists: info.value!.artists,
                styles: info.value!.styles,
                relate_styles: info.value!.relate_styles,
                links: info.value!.links,
                online_links: info.value!.online_links,
                primary_img: info.value!.primary_img,
            });
        };

        const albumCachedIndex = computed(() => {
            return store.state.albumCache.findIndex((album) => {
                return album._id === info.value?._id;
            });
        });

        const handlerCacheRemove = () => {
            store.commit("removeAlbumCacheById", info.value!._id);
        };
        const handleShowThirdEditor = () => {
            dialogFormVisible.value = true;
            albumEditor.value.isCombine = 2;
        };

        return {
            id,
            info,
            now_heart,
            loadings,
            heart_loading,
            doHeart,
            online_link_name,
            rocknuts,
            dataGetted,
            activeOnlineLink,
            activeOnlineLinkUrl,
            userInfo,
            handlerCreateArticle,
            albumEditor,
            handleShowThirdEditor,
            dialogFormVisible,
            showEditer,
            refreshData,
            doLockInfo,

            dialogAddRankFormVisible,
            handlerAddToRank,
            albumCachedIndex,
            handlerCacheAdd,
            handlerCacheRemove,
            route,
        };
    },
});
